import React, { useContext, useMemo } from "react";
import PropTypes from "prop-types";
import * as R from "ramda";
import { Icon, Tooltip, Avatar, Checkbox } from "antd";
import { useTranslation } from "react-i18next";
import moment from "moment";
import hexAlpha from "hex-alpha";
import { useSelector } from "react-redux";

import { getOptimizedAvatar } from "../../utils/images";
import { formatAddress } from "../../utils/candidates.js";
import Button from "../Button";
import { initials } from "../../utils/strings";
import { ACCEPT_LANGUAGE } from "../../locales/acceptedLanguages";
import Date from "../Date";
import { CANDIDATE_LISTS } from "../../constants/opportunity";
import { getCountry } from "../../utils/data/static/countries";
import {
  tagColors,
  SuccessProbabilityTag
} from "../CandidateModal/Tests/SuccessProbabilityTag";

import CardAction from "./CardAction";
import { ConditionalWrapper } from "./../ConditionalWrapper";
import MatchPercentage from "./MatchPercentage";

import styled, { ThemeContext } from "styled-components/macro";

const accesibilityKeys = ["Enter", " "];

const TooltipComponentWrapper = styled.div`
  align-items: center;
  display: flex;
  font-size: 0.75rem;
  justify-content: center;
`;
const TooltipText = styled.span`
  padding-left: ${({ icon }) => (icon ? 0.5 : 0)}rem;
`;

const TooltipComponent = ({ children, icon }) => (
  <TooltipComponentWrapper>
    {icon && <Icon type={icon} style={{ fontSize: "20px" }} />}
    <TooltipText icon={Boolean(icon)}>{children}</TooltipText>
  </TooltipComponentWrapper>
);

const Wrapper = styled.div`
  border-left: ${({ theme, viewed }) =>
    viewed ? "" : `2px solid ${theme["@primary"]}`};
  box-shadow: ${({ highlight, isSelected, viewed }) =>
    highlight
      ? `0 1px 3px 0 rgba(0, 0, 0, 0.05), 0 1px ${
          isSelected ? 8 : 6
        }px 0 ${hexAlpha(highlight, isSelected ? 0.9 : 0.5)}`
      : viewed
      ? ""
      : "0 1px 3px 0 rgba(0, 0, 0, 0.15)"};

  background-color: ${({ isRegistered }) =>
    isRegistered ? "#ffffff" : "#e5e9ec"};

  display: flex;
  flex-direction: row;
  transition: box-shadow 0.4s ease;
  position: relative;

  :hover,
  :focus {
    box-shadow: ${({ highlight, isSelected }) =>
      highlight
        ? `0 1px 6px 0 rgba(0, 0, 0, ${isSelected ? 0.2 : 0.1}), 0 2px ${
            isSelected ? 12 : 10
          }px 0 ${hexAlpha(highlight, isSelected ? 0.7 : 0.6)}`
        : "0 1px 8px 0 rgba(0, 0, 0, 0.2)"};
    outline: 0;
  }

  &.clickable {
    cursor: pointer;
  }

  .ant-avatar {
    cursor: pointer;
    background: ${({ theme }) => theme["@gray-50"]};
  }

  .side {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 16px 12px;
    background-color: #f7f9fa;
    max-width: 67px;
    position: relative;
  }

  .success-tag-border {
    border-left: solid 2px ${({ tag }) => tagColors(tag)};
  }

  .center {
    display: flex;
    justify-content: center;
    align-items: flex-end;
    flex-grow: 2;
  }

  .success-tag {
    font-size: 10px;
    line-height: 1.2;
    border-radius: 8px;
    height: 16px;
    text-align: center;
    padding: 1px 6px;
    font-weight: bold;
  }

  .tags {
    display: flex;
    flex-direction: row;
    width: 1000px;
    margin: 0 -4px;
  }

  .tags-container {
    display: flex;
    flex-direction: column;
    overflow: hidden;
    position: relative;
    &:after {
      content: "";
      position: absolute;
      top: 0;
      right: 0;
      width: 20px;
      height: 20px;
      background: linear-gradient(
        90deg,
        rgba(255, 255, 255, 0) 0%,
        ${({ isRegistered }) =>
          isRegistered
            ? "rgba(255, 255, 255, 1) 100%"
            : "rgba(229, 233, 236, 1) 100%"}
      );
    }
  }

  .info {
    min-width: 0;
    padding: 16px 12px;
    display: flex;
    flex: 1;
    flex-direction: column;
    justify-content: space-between;
    align-items: stretch;
    .name-wrapper {
      display: flex;
      flex-direction: row;
    }
    ul {
      list-style-type: none;
    }
  }

  &.compact {
    min-height: unset;
    height: 48px;
    .side {
      flex-direction: row;
      padding: 3px;
      gap: 4px;
      max-width: none;
    }
    .info > *:not(.top),
    .top ul {
      display: none;
    }
    .info {
      padding: 0;
    }
    .top {
      height: 48px;
      padding: 0 12px;
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: stretch;
      .name-wrapper {
        flex: 1;
        justify-content: space-between;
        align-items: center;
      }
    }
    h2 {
      margin: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      max-width: 142px;
      display: inline-block;
    }
  }
`;

const Name = styled.h2`
  font-size: 16px;
  line-height: 1.5;
  font-weight: ${({ viewed }) => (viewed ? "normal" : "600")};
  color: #343a40;
  cursor: pointer;
  flex: 1;
  transition: 0.3s ease;
  margin: 0;
  word-break: break-word;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  &:hover {
    color: ${({ theme }) => theme["@primary"]};
  }
`;

const SelectedCheckbox = styled(Checkbox)`
  .ant-checkbox {
    margin-top: -3px;
    margin-right: 1px;
    padding: 8px;
  }
`;

const Tag = styled.span`
  display: block;
  height: 16px;
  border-radius: 8px;
  border: solid 1px #e5e9ed;
  background-color: #ffffff;
  padding: 1px 9px;
  font-size: 10px;
  color: #747d84;
  line-height: 1.2;
  text-align: center;
  margin: 0 5px 8px;
  min-width: 40px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PreviousMatchTag = styled(Tag)`
  text-transform: uppercase;
  align-self: start;
  padding: 1px 11px;
  min-width: 172px;
  margin-inline: 0;
  background-color: #dbe2e6;
  border-color: #e7ecef;
  color: #5f676d;

  i {
    font-size: 12px;
    margin-right: 5px;
  }
  span {
    vertical-align: top;
  }
`;

const StatusTag = styled(Tag)`
  text-transform: uppercase;
  align-self: start;
  padding: 1px 16px;
  min-width: 172px;
  margin: 0;
  ${({ color, dark }) =>
    color &&
    `
    background-color: ${color};
    border-color: ${color};
    color: ${dark ? "black" : "white"};
  `};
`;

const InfoTitle = styled.h3`
  font-size: 12px;
  line-height: 1.5;
  color: #343a40;
  margin: 0;
  position: relative;
  padding-left: 14px;
  .anticon {
    color: #747c84;
    font-size: 10px;
    position: absolute;
    left: 0;
    top: 4px;
  }
`;

const InfoDescription = styled.p`
  font-size: 10px;
  line-height: 1.4;
  color: #747c84;
  margin-bottom: 0;
  margin-left: 14px;
`;

const InfoItemWrapper = styled.li`
  display: flex;
  flex-direction: row;
  font-weight: normal;
  margin: 10px 0;
`;

const EmptyText = styled.p`
  font-size: 12px;
  color: #949ca2;
  font-style: italic;
  margin: 0;
`;

const TextExperience = styled.p`
  font-size: 12px;
  line-height: 1.5;
  color: #343a40;
  margin: 0;
`;

const EmptyContainer = styled.div`
  display: flex;
  align-items: center;
  margin: 10px 0;
  position: relative;
  padding-left: 14px;
  .anticon {
    color: #747c84;
    font-size: 10px;
    margin-right: 4px;
    position: absolute;
    left: 0;
    top: 4px;
  }
`;

const DisabledContainer = styled.div`
  align-items: center;
  background-color: #ffffff77;
  display: flex;
  height: 100%;
  justify-content: center;
  left: 0;
  position: absolute;
  top: 0;
  width: 100%;
  z-index: 2;
`;

const Title = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const RecentIcon = styled(Icon)`
  position: absolute;
  left: 44px;
  top: 14px;
  z-index: 10;
  font-size: 17px;
`;

const InfoItem = ({ icon, title, description }) => (
  <InfoItemWrapper>
    <div className="col">
      <InfoTitle>
        <Icon theme="filled" type={icon} />
        <Title>{typeof title === "string" && title}</Title>
      </InfoTitle>
      <InfoDescription>{description}</InfoDescription>
    </div>
  </InfoItemWrapper>
);

const WrapperInteraction = styled.div`
  height: 229px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05);
  background-color: ${({ isRegistered }) =>
    isRegistered ? "#ffffff" : "#e5e9ec"};

  display: flex;
  flex-direction: column;
  transition: box-shadow 0.4s ease;
  position: relative;
  cursor: pointer;

  :hover,
  :focus {
    box-shadow: 0 1px 8px 0 rgba(0, 0, 0, 0.2);
    outline: 0;
  }

  .side {
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 16px 12px;
    background-color: ${({ theme }) => theme["@gray-20"]};
  }
  .ant-avatar {
    cursor: pointer;
    background: #bfbfbf;
    margin-right: 10px;
  }

  .content {
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
  }
`;

const IconInteraction = styled(Icon)`
  margin-bottom: 5px;
  font-size: 22px;
  svg {
    color: ${({ theme }) => theme["@gray-50"]};
  }
`;

const Content = styled.span`
  text-align: center;
  color: ${({ theme }) => theme["@gray-50"]};
`;

const INTERACTION_ICON_MAP = {
  selected: "check-circle",
  rejected: "minus-circle",
  process: "swap"
};

const CandidateCard = ({
  onSelectCandidate,
  candidate,
  actions = [],
  highlight,
  status,
  statusTooltip,
  statusDataTest = "",
  tags = [],
  bottomButton,
  compact,
  onClick,
  omitExternalTag = false,
  isSelected,
  onSelect,
  loading,
  disabled,
  isRecent,
  list,
  viewed = true,
  ...props
}) => {
  const { t, i18n } = useTranslation();
  const theme = useContext(ThemeContext);
  const locale = ACCEPT_LANGUAGE[i18n.language] || ACCEPT_LANGUAGE.DEFAULT;
  moment.locale(locale);

  const name = candidate.fullname || `${candidate.first} ${candidate.last}`;
  const experience = R.propOr(null, "experience", candidate);

  const currentPosition = useMemo(() => {
    return R.pipe(
      R.propOr([], "lastPositions"),
      R.find(R.prop("current"))
    )(candidate);
  }, [candidate]);

  const lastPosition = useMemo(() => {
    return R.pipe(
      R.propOr([], "lastPositions"),
      R.sortBy(R.prop("end")),
      R.last
    )(candidate);
  }, [candidate]);

  const isRegistered = useMemo(
    // TODO change propOr to prop
    () => R.propOr(true, "isRegistered")(candidate),
    [candidate]
  );

  const addressInfo = useMemo(
    () =>
      formatAddress({
        country: getCountry(candidate.country),
        province: candidate.province?.name,
        district: candidate.district?.name
      }),
    [candidate]
  );

  const handleFocusedKeyPress = event => {
    if (R.includes(event.key, accesibilityKeys) && !(loading || disabled)) {
      if (onClick && !onSelect) onClick(event);
      if (onSelect) onSelect(!isSelected);
    }
  };

  const hasInteraction = useMemo(
    () => R.propOr(false, "interaction", candidate),
    [candidate]
  );

  const applyTime = useMemo(
    () =>
      candidate.opportunityProcessStatus &&
      candidate.opportunityProcessStatus.length &&
      candidate.opportunityProcessStatus[0].applyTime,
    [candidate]
  );

  const hasRank = useSelector(state => {
    let rank = R.pathOr(undefined, ["company", "services", "rank"], state);
    if (rank === undefined) {
      rank = R.pathOr(false, ["headquarter", "services", "rank"], state);
    }
    return rank;
  });

  const hasScore = useMemo(() => hasRank && candidate.score > 0, [
    hasRank,
    candidate
  ]);

  const hasSuccessProbabilityTest = useSelector(state => {
    let res = R.pathOr(
      undefined,
      ["company", "services", "successProbabilityTest"],
      state
    );
    if (res === undefined) {
      res = R.pathOr(
        false,
        ["headquarter", "services", "successProbabilityTest"],
        state
      );
    }
    return res;
  });

  const tagProfile = useSelector(state => {
    if (!hasSuccessProbabilityTest) return;
    const op = R.path(["opportunity", "selected"], state);
    if (!op || R.isEmpty(op)) return;
    return R.propOr(0, "successProbabilityProfile", op);
  });

  const successProbabilityTag = useMemo(() => {
    if (tagProfile === undefined) return;
    const tests = R.propOr([], "tests", candidate);
    const test =
      R.find(
        R.both(R.propEq("source", "worcket"), R.propEq("testIdentifier", "3")),
        tests
      ) || {};
    const result =
      R.find(
        R.propEq("idProfile", tagProfile),
        test.serviceCallResultData || []
      ) || {};
    return result.tag;
  }, [candidate, tagProfile]);

  return hasInteraction ? (
    <WrapperInteraction
      data-test={`candidate-card-interaction-${candidate.interaction}`}
      onClick={onClick}
      onKeyPress={handleFocusedKeyPress}
      isRegistered={isRegistered}
      tag={successProbabilityTag}
      {...props}
    >
      <div
        className={`side ${successProbabilityTag ? "success-tag-border" : ""}`}
      >
        <Avatar
          size={48}
          src={candidate.avatar && getOptimizedAvatar(candidate.avatar)}
          alt={`${name} avatar`}
          onClick={onSelectCandidate}
        >
          {initials(candidate)}
        </Avatar>
        <Name
          data-test="candidate-card-name"
          onClick={onSelectCandidate}
          viewed={viewed}
        >
          {name}
        </Name>
      </div>
      <div className="content">
        <IconInteraction type={INTERACTION_ICON_MAP[candidate.interaction]} />
        <Content>
          {t("components:CandidateCard.candidate")}
          <br />
          {t(`components:CandidateCard.${candidate.interaction}`)}
        </Content>
      </div>
    </WrapperInteraction>
  ) : (
    // TODO add ARIA attributes
    <Wrapper
      viewed={viewed}
      isRecent={isRecent}
      isSelected={isSelected}
      tabIndex="0"
      data-test="candidate-card"
      className={`${compact ? "compact" : ""} ${onClick ? "clickable" : ""}`}
      highlight={onSelect && isSelected && theme["@primary"]}
      onClick={onClick}
      onKeyPress={handleFocusedKeyPress}
      isRegistered={isRegistered}
      tag={successProbabilityTag}
      {...props}
    >
      {disabled ||
        (loading && (
          <DisabledContainer>
            {loading && (
              <Icon
                style={{
                  color: theme["@primary"],
                  fontSize: 36
                }}
                type="loading"
              />
            )}
          </DisabledContainer>
        ))}
      <div
        className={`side ${successProbabilityTag ? "success-tag-border" : ""}`}
      >
        {isRecent && (
          <Tooltip
            placement="top"
            title={t("opportunity:candidates.recentPostulationTooltip")}
          >
            <RecentIcon
              type="check-circle"
              theme="filled"
              style={{
                color: theme["@primary"],
                background: "white",
                borderRadius: "50%"
              }}
            />
          </Tooltip>
        )}
        <Tooltip
          placement="top"
          title={
            list === CANDIDATE_LISTS.suggested &&
            hasScore &&
            applyTime &&
            moment(applyTime).format("LLL")
          }
        >
          <Avatar
            size={!compact ? 48 : 24}
            src={candidate.avatar && getOptimizedAvatar(candidate.avatar)}
            alt={`${name} avatar`}
            onClick={onSelectCandidate}
            style={!compact ? { marginBottom: 16 } : {}}
          >
            {initials(candidate)}
          </Avatar>
        </Tooltip>
        {R.includes(list, [
          CANDIDATE_LISTS.suggested,
          CANDIDATE_LISTS.hunting
        ]) &&
          (hasScore ? (
            <MatchPercentage score={candidate.score} />
          ) : (
            <>
              {applyTime ? (
                <Date
                  value={applyTime}
                  fontSizeDay={18}
                  fontSizeMonth={9}
                  tooltip={t("opportunity:candidates.postulationDate")}
                  highlighted={isRecent}
                />
              ) : (
                <div />
              )}
            </>
          ))}
        {actions
          .filter(Boolean)
          .map(
            action =>
              action.customRender || (
                <CardAction
                  compact={compact}
                  size={compact ? 16 : undefined}
                  key={action.name}
                  {...action}
                  disabled={action.disabled || loading || disabled}
                />
              )
          )}
        {!compact && successProbabilityTag && (
          <div className="center">
            <SuccessProbabilityTag
              tag={successProbabilityTag}
              className="success-tag"
            />
          </div>
        )}
      </div>
      <div className="info">
        <div className="top">
          <div className="name-wrapper">
            <Name
              title={compact && name}
              data-test="candidate-card-name"
              onClick={onSelectCandidate}
              viewed={viewed}
            >
              {name}
            </Name>
            {onSelect && (
              <SelectedCheckbox
                disabled={loading || disabled}
                checked={isSelected}
                data-test="select-candidate-checkbox"
                onChange={event => onSelect(event.target.checked)}
                onClick={event => {
                  event.stopPropagation();
                }}
              ></SelectedCheckbox>
            )}
          </div>
          <ul data-test="info-item-data">
            {currentPosition ? (
              <InfoItem
                icon="shop"
                title={currentPosition.title}
                description={
                  <>
                    {currentPosition.company && `en ${currentPosition.company}`}
                    {currentPosition.start && (
                      <span data-test="info-current-position">
                        <br />
                        {`${moment(currentPosition.start).format(
                          "MMMM YYYY"
                        )} - `}
                        <b>
                          {t("opportunity:process.candidateDetails.present")}
                        </b>
                      </span>
                    )}
                  </>
                }
              />
            ) : (
              <>
                {lastPosition ? (
                  <InfoItem
                    icon="shop"
                    title={lastPosition.title}
                    description={
                      <>
                        {lastPosition.company && `en ${lastPosition.company}`}
                        {lastPosition.start && (
                          <span data-test="info-last-position">
                            <br />
                            {`${moment(lastPosition.start).format(
                              "MMMM YYYY"
                            )} - ${moment(lastPosition.end).format(
                              "MMMM YYYY"
                            )}`}
                          </span>
                        )}
                      </>
                    }
                  />
                ) : (
                  <EmptyContainer>
                    <Icon theme="filled" type="shop" />
                    {!R.isNil(experience) ? (
                      <TextExperience>
                        {experience === 0 ? (
                          t(
                            "opportunity:process.candidateDetails.experience.noExperience"
                          )
                        ) : (
                          <>
                            {t(
                              `catalogs:experience.${experience
                                .toString()
                                .replace(".", ",")}`
                            )}
                            {t(
                              "opportunity:process.candidateDetails.experience.years"
                            )}
                          </>
                        )}
                      </TextExperience>
                    ) : (
                      <EmptyText>{t(`global:emptyLastPosition`)}</EmptyText>
                    )}
                  </EmptyContainer>
                )}
              </>
            )}
            {(candidate.educationLevel &&
              candidate.educationLevel.length > 0) ||
            (candidate.degrees && candidate.degrees.length > 0) ||
            (candidate.universities && candidate.universities.length > 0) ? (
              <InfoItem
                icon="bank"
                title={
                  candidate.educationLevel
                    ? t(
                        `catalogs:educationLevelsAdvanced.${candidate.educationLevel}`
                      )
                    : candidate.degrees
                    ? R.pipe(
                        R.propOr([], "degrees"),
                        R.take(1)
                      )(candidate)[0]
                    : R.takeLast(1, candidate.universities)
                }
              />
            ) : (
              <EmptyContainer>
                <Icon theme="filled" type="bank" />
                <EmptyText>{t(`global:emptyEducationLevel`)}</EmptyText>
              </EmptyContainer>
            )}
            {addressInfo && <InfoItem icon="environment" title={addressInfo} />}
          </ul>
        </div>
        <div className="tags-container">
          {tags.length > 0 && (
            <div className="tags">
              {R.flatten(tags).map((tag, i) => (
                <Tag key={i} title={tag}>
                  {tag}
                </Tag>
              ))}
            </div>
          )}
          {candidate.previousMatch && (
            <PreviousMatchTag
              title={t("components:CandidateCard.previousMatch")}
            >
              <Icon type="swap" />
              <span>{t("components:CandidateCard.previousMatch")}</span>
            </PreviousMatchTag>
          )}
          {(status || (!isRegistered && !omitExternalTag)) && (
            <ConditionalWrapper
              condition={
                statusTooltip ||
                (!isRegistered &&
                  t("components:CandidateCard.externalCandidateTooltip"))
              }
              trueWrapper={children => (
                <Tooltip
                  placement="top"
                  title={
                    <TooltipComponent icon={!isRegistered && "info-circle"}>
                      {statusTooltip ||
                        (!isRegistered &&
                          t(
                            "components:CandidateCard.externalCandidateTooltip"
                          ))}
                    </TooltipComponent>
                  }
                >
                  {children}
                </Tooltip>
              )}
            >
              <StatusTag
                data-test={`candidate-card-status-${statusDataTest}`}
                color={highlight || (!isRegistered && "#d0d6da")}
                title={
                  status ||
                  (!isRegistered &&
                    t("components:CandidateCard.externalCandidate"))
                }
                dark={!status && !isRegistered}
              >
                {status ||
                  (!isRegistered &&
                    t("components:CandidateCard.externalCandidate"))}
              </StatusTag>
            </ConditionalWrapper>
          )}
        </div>
        {bottomButton && (
          <Button
            type="primary"
            size="sm"
            style={{ marginTop: 16 }}
            disabled={disabled}
            {...bottomButton}
          >
            {bottomButton.name}
          </Button>
        )}
      </div>
    </Wrapper>
  );
};

const ActionPropType = PropTypes.oneOfType([
  PropTypes.shape({ customRender: PropTypes.node }),
  PropTypes.shape({
    // Side actions array
    name: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
    loading: PropTypes.bool,
    confirm: PropTypes.string,
    icon: PropTypes.string.isRequired,
    color: PropTypes.string,
    iconColor: PropTypes.string
  })
]);

CandidateCard.propTypes = {
  onSelectCandidate: PropTypes.func,
  actions: PropTypes.arrayOf(ActionPropType),
  highlight: PropTypes.string, // Highlight color
  status: PropTypes.string, // Status text
  statusTooltip: PropTypes.string,
  tags: PropTypes.arrayOf(PropTypes.string),
  bottomButton: ActionPropType,
  compact: PropTypes.bool
};

export default CandidateCard;
